import { useState, useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Check } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost } from '@/lib/api'
import NfcTagProgrammer from '@/components/NfcTagProgrammer'

interface Category {
  id: string
  name: string
  code: string
  children: { id: string; name: string; code: string }[]
}

interface Building {
  id: string
  name: string
  code: string
  floors: Floor[]
}

interface Floor {
  id: string
  name: string
  number: number
  rooms: Room[]
}

interface Room {
  id: string
  name: string
  number: string
}

interface PropertyDetail {
  id: string
  name: string
  buildings: Building[]
}

type Step = 'details' | 'tag'

export function AssetCreatePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentPropertyId } = useAuthStore()
  const pid = currentPropertyId

  const [step, setStep] = useState<Step>('details')
  const [savedAssetId, setSavedAssetId] = useState<string | null>(null)
  const [savedAssetName, setSavedAssetName] = useState('')

  const [form, setForm] = useState({
    name: '',
    code: '',
    categoryId: '',
    description: '',
    buildingId: '',
    floorId: '',
    roomId: '',
    criticality: 'MEDIUM',
    make: '',
    model: '',
    serialNumber: '',
    assetTag: '',
    supplier: '',
    condition: '',
    purchaseCostGbp: '',
    notes: '',
    trackMovement: false,
  })

  const { data: categories } = useQuery({
    queryKey: ['asset-categories', pid],
    queryFn: () => apiGet<Category[]>(`/properties/${pid}/assets/categories/all`),
    enabled: !!pid,
  })

  const { data: property } = useQuery({
    queryKey: ['property-detail', pid],
    queryFn: () => apiGet<PropertyDetail>(`/properties/${pid}`),
    enabled: !!pid,
  })

  const buildings = property?.buildings ?? []
  const selectedBuilding = buildings.find((b) => b.id === form.buildingId)
  const floors = selectedBuilding?.floors ?? []
  const selectedFloor = floors.find((f) => f.id === form.floorId)
  const rooms = selectedFloor?.rooms ?? []

  const allCategories = useMemo(() => {
    if (!categories) return []
    const flat: { id: string; name: string; group: string }[] = []
    for (const cat of categories) {
      flat.push({ id: cat.id, name: cat.name, group: '' })
      for (const child of cat.children) {
        flat.push({ id: child.id, name: child.name, group: cat.name })
      }
    }
    return flat
  }, [categories])

  const createMutation = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {
        name: form.name,
        code: form.code,
        categoryId: form.categoryId,
        criticality: form.criticality,
      }
      if (form.description) body.description = form.description
      if (form.buildingId) body.buildingId = form.buildingId
      if (form.floorId) body.floorId = form.floorId
      if (form.roomId) body.roomId = form.roomId
      if (form.make) body.make = form.make
      if (form.model) body.model = form.model
      if (form.serialNumber) body.serialNumber = form.serialNumber
      if (form.assetTag) body.assetTag = form.assetTag
      if (form.notes) body.notes = form.notes
      if (form.purchaseCostGbp) body.purchaseCostGbp = parseFloat(form.purchaseCostGbp)
      return apiPost(`/properties/${pid}/assets`, body)
    },
    onSuccess: (asset: any) => {
      setSavedAssetId(asset.id)
      setSavedAssetName(asset.name)
      setStep('tag')
      queryClient.invalidateQueries({ queryKey: ['assets'] })
    },
  })

  const canSubmit = form.name && form.code && form.categoryId

  // ── Step 2: Tag ──
  if (step === 'tag' && savedAssetId) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <Check className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-navy-800">Asset Created</h1>
            <p className="text-sm text-gray-500">
              "{savedAssetName}" saved — now attach a tag so it can be scanned.
            </p>
          </div>
        </div>

        <div className="card">
          <NfcTagProgrammer
            assetId={savedAssetId}
            assetName={savedAssetName}
          />
        </div>

        <div className="flex gap-3">
          <Link
            to={`/assets/${savedAssetId}`}
            className="flex-1 text-center py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Skip — do this later
          </Link>
          <Link
            to={`/assets/${savedAssetId}`}
            className="flex-1 text-center py-2.5 bg-navy-800 text-white rounded-lg text-sm font-medium hover:bg-navy-700"
          >
            Done — View Asset
          </Link>
        </div>
      </div>
    )
  }

  // ── Step 1: Details ──
  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/assets')} className="text-gray-400 hover:text-navy-800">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-navy-800">New Asset</h1>
      </div>

      <div className="card space-y-5">
        {/* Required fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. King Divan Bed"
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asset Code *</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="e.g. BED-204"
              className="input font-mono"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <select
            value={form.categoryId}
            onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
            className="input"
          >
            <option value="">Select category...</option>
            {allCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.group ? `${cat.group} > ${cat.name}` : cat.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Optional description..."
            rows={2}
            className="input"
          />
        </div>

        {/* Location */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Location</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select
              value={form.buildingId}
              onChange={(e) => setForm((f) => ({ ...f, buildingId: e.target.value, floorId: '', roomId: '' }))}
              className="input"
            >
              <option value="">Building...</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <select
              value={form.floorId}
              onChange={(e) => setForm((f) => ({ ...f, floorId: e.target.value, roomId: '' }))}
              className="input"
              disabled={!form.buildingId}
            >
              <option value="">Floor...</option>
              {floors.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <select
              value={form.roomId}
              onChange={(e) => setForm((f) => ({ ...f, roomId: e.target.value }))}
              className="input"
              disabled={!form.floorId}
            >
              <option value="">Room...</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.id}>{r.number} - {r.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Criticality */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Criticality</label>
          <select
            value={form.criticality}
            onChange={(e) => setForm((f) => ({ ...f, criticality: e.target.value }))}
            className="input w-auto"
          >
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
        </div>

        {/* Manufacturer */}
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Manufacturer / Model</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text"
              value={form.make}
              onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))}
              placeholder="Make"
              className="input"
            />
            <input
              type="text"
              value={form.model}
              onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
              placeholder="Model"
              className="input"
            />
            <input
              type="text"
              value={form.serialNumber}
              onChange={(e) => setForm((f) => ({ ...f, serialNumber: e.target.value }))}
              placeholder="Serial Number"
              className="input font-mono"
            />
          </div>
        </div>

        {/* Financial */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
            <input
              type="text"
              value={form.supplier}
              onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))}
              placeholder="Supplier name"
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Cost (GBP)</label>
            <input
              type="number"
              step="0.01"
              value={form.purchaseCostGbp}
              onChange={(e) => setForm((f) => ({ ...f, purchaseCostGbp: e.target.value }))}
              placeholder="0.00"
              className="input"
            />
          </div>
        </div>

        {/* Condition */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
          <select
            value={form.condition}
            onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}
            className="input w-auto"
          >
            <option value="">Not assessed</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Fair">Fair</option>
            <option value="Poor">Poor</option>
          </select>
        </div>

        {/* Movement tracking toggle */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, trackMovement: !f.trackMovement }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              form.trackMovement ? 'bg-navy-800' : 'bg-gray-200'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              form.trackMovement ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
          <div>
            <p className="text-sm font-medium text-gray-700">Track movement</p>
            <p className="text-xs text-gray-400">Log dispatch, arrival and location scans for this asset</p>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder="Any additional notes..."
            rows={2}
            className="input"
          />
        </div>

        {/* Error */}
        {createMutation.isError && (
          <p className="text-sm text-red-600">
            {(createMutation.error as any)?.response?.data?.error ?? 'Failed to create asset'}
          </p>
        )}

        {/* Submit */}
        <button
          type="button"
          onClick={() => createMutation.mutate()}
          disabled={!canSubmit || createMutation.isPending}
          className="w-full py-3 bg-navy-800 text-white rounded-xl font-semibold hover:bg-navy-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {createMutation.isPending ? 'Saving...' : 'Save & Tag Asset'}
        </button>
      </div>
    </div>
  )
}
